import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Button,
  MenuItem,
  TextField,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import ItemisedBillReportService from 'services/ItemisedBillReportService';
import SalespersonsService from 'services/SalesPersonService';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { saveAs } from 'file-saver';
import 'date-fns';
import ItemSearchSelect from 'components/utils/ItemSearchSelect/ItemSearchSelect';
import ItemSummaryReportService from 'services/ItemSummaryReportService';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import AlertHelper from 'helpers/AlertHelper';
import Error from 'components/utils/Alert/Error';
import { ShopContext } from 'Context/ShopContext';
import { AlertMessagesContext } from 'react-alert-messages';
import CsvHelper from 'helpers/CsvHelper';
import { filterEndDate } from 'components/utils/FilterEndDate';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import TerminalService from 'services/TerminalServices';
import { SelectFilterStyle, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { InventoryContext } from 'Context/InventoryContext';
import { useTranslation } from 'react-i18next';

function ReturnedBillItemSummaryReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { categories, getCategories } = useContext(InventoryContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [terminals, setTerminals] = useState([]);
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTax, setFilterTax] = useState('all');
  const [filterItem, setFilterItem] = useState('all');
  const [sorting, setSorting] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [taxes, setTaxes] = useState('');
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    filterItem,
    filterCategory,
    sorting,
    filterTax,
  });
  const [errorMsg, setErrorMsg] = useState();
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    bill_no: {
      title: tCommon('Bill No'),
      getValue: (datum) => datum.bill_no || datum.fid,
    },
    plu: tCommon('PLU'),
    name: tCommon('Item Name'),
    date: tCommon('Date'),
    time: {
      title: tCommon('Time'),
      getValue: (datum) => DateTimeHelpers.formatServerTimeSegment(datum.time),
    },
    price: tCommon('Unit Price'),
    tax: tCommon('Tax(%)'),
    tax_amount: {
      title: tCommon('Tax Amount'),
      getValue: (datum) => ShopsHelper.getAmountFormatted(shop, datum?.tax_amount || 0),
    },
    cess: tCommon('Additional Tax(%)'),
    cess_amount: {
      title: tCommon('Additional Tax'),
      getValue: (datum) => ShopsHelper.getAmountFormatted(shop, datum?.cess_amount || 0),
    },
    quantity: tCommon('Sold Quantity'),
    total: {
      title: tCommon('Total Amount'),
      getValue: (datum) => ShopsHelper.getAmountFormatted(shop, datum?.total || 0),
    },
  };

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      filterItem !== prevFilterData.filterItem ||
      filterCategory !== prevFilterData.filterCategory ||
      sorting !== prevFilterData.sorting ||
      filterTax !== prevFilterData.filterTax ||
      filterTerminal !== prevFilterData.filterTerminal ||
      filterSalesperson !== prevFilterData.filterSalesperson
    );
  }, [
    filterFromDate,
    filterToDate,
    filterItem,
    filterCategory,
    sorting,
    filterTax,
    filterTerminal,
    filterSalesperson,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.filterItem,
    prevFilterData.filterCategory,
    prevFilterData.sorting,
    prevFilterData.filterTax,
    prevFilterData.filterTerminal,
    prevFilterData.filterSalesperson,
  ]);

  useEffect(() => {
    generateItemisedBillReports();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getVat();
    getSalespersons();
    getTerminals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isShowGenerateButton, isAnyChangeOnReportsFilters]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Returned Sale Item Summary Report'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredReportData,
      {
        [tCommon('Total Tax')]: `${shop?.currency || ''} ${summary.totalTax}`,
        [tCommon('Total Additional Tax')]: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
        [tCommon('Total Amount')]: `${shop?.currency || ''} ${summary?.totalAmount}`,
      }
    );
  };

  const generateItemisedBillReports = () => {
    const { startDate, endDate } = dynamicDateRange('RETURNED_BILL_ITEM_SUMMARY');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadItemisedBillReports({
      startTime: startDate,
      endTime: endDate,
      sort: sorting,
      category: filterCategory,
      item: filterItem,
      tax: filterTax,
      cashier_ids: filterSalesperson,
      terminal_ids: filterTerminal,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      filterItem,
      filterCategory,
      sorting,
      filterTax,
      filterSalesperson,
      filterTerminal,
    });
  };

  const getSalespersons = async () => {
    const res = await SalespersonsService.getSalesPersons();
    setSalesPersons(res);
  };

  const getTerminals = async () => {
    const res = await TerminalService.getTerminals();
    setTerminals(res);
  };

  const getVat = async () => {
    const res = await ItemSummaryReportService.getVat();
    setTaxes(res);
  };

  const toCsvBtnPressed = async () => {
    const titleLine = tReport('Returned Sale Item Summary Report for {{from}} to {{to}}', {
      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
      to: DateTimeHelpers.convertDateToDMY(filterToDate),
    });

    const csvWithoutTotal = CsvHelper.getString(filteredReportData, CSV_COLUMNS, titleLine);

    // Create total row
    const summary = _getItemisedReportSummary(filteredReportData);
    const fields = Object.keys(CSV_COLUMNS);
    const totalRowValues = fields.map((key) => {
      switch (key) {
        case 'bill_no':
          return tCommon('Total');
        case 'total':
          return summary.totalAmount;
        case 'tax_amount':
          return summary.totalTax;
        case 'cess_amount':
          return summary.totalAdditionalTax;
        default:
          return '';
      }
    });

    const totalRow = totalRowValues.join(',') + '\n';
    const csv = csvWithoutTotal + '\n' + totalRow;

    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `RETURNED-SALES-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const loadItemisedBillReports = async ({
    startTime = filterFromDate,
    endTime = filterToDate,
    sort = sorting,
    category = filterCategory,
    item = filterItem,
    tax = filterTax,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
  } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }
    setLoadingIndicator(true);
    try {
      const timezoneFreeStartTime = DateTimeHelpers.getTimezoneOffsetDate(startTime);
      const timezoneFreeEndTime = DateTimeHelpers.getTimezoneOffsetDate(endTime);
      const data = {
        filter_start_time: timezoneFreeStartTime,
        filter_end_time: timezoneFreeEndTime,
        sort_bill_num: sort,
        category_id: category === 'all' ? undefined : category,
        filter_name: item === 'all' ? undefined : [item],
        filter_tax: tax === 'all' ? undefined : [tax],
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
      };
      const resp = await ItemisedBillReportService.getReturnedBillItemReport(data);
      setReportData(resp);
    } catch (err) {
      AlertHelper.setShortMessage(setErrorMsg, err.message);
    }
    setLoadingIndicator(false);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const headerData = [
    {
      label: tCommon('Bill No'),
      id: 'bill_no',
      type: 'callback',
      viewRender: (obj) => obj.bill_no || obj.fid,
    },
    {
      label: tCommon('PLU'),
      id: 'plu',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'text',
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.formatServerTimeSegment(item.time),
    },
    {
      label: tCommon('Unit Price'),
      id: 'price',
      type: 'floatAmount',
    },
    {
      label: tCommon('Tax(%)'),
      id: 'tax',
      type: 'text',
    },
    {
      label: tCommon('Tax'),
      id: 'tax_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Additional Tax(%)'),
      id: 'cess',
      type: 'text',
    },
    {
      label: tCommon('Additional Tax'),
      id: 'cess_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Sold Quantity'),
      id: 'quantity',
      type: 'callback',
      viewRender: (item) => {
        if (!item.quantity || isNaN(item.quantity)) return;
        return `${ShopsHelper.getAmountFormatted(shop, item.option_quantity ? item.option_quantity : item.quantity)} ${
          item.unit || ''
        }`;
      },
    },
    {
      label: tCommon('Total Amount'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item?.total)}`;
      },
    },
  ];

  const _getItemisedReportSummary = (items) => {
    let totalAmount = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;
    const salesItems = items.filter(
      (item) => item.name?.toLowerCase() !== 'discount' && String(item.plu) !== '9999996'
    );

    salesItems.forEach((item) => {
      totalAmount += item?.total;
      totalTax += item.tax_amount;
      totalAdditionalTax += item.cess_amount;
    });
    return {
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
    };
  };

  const filteredReportData = reportData.filter(
    (datum) =>
      datum.bill_no.toLowerCase().includes(searchText.toLowerCase()) ||
      datum.name.toLowerCase().includes(searchText.toLowerCase())
  );
  const summary = _getItemisedReportSummary(filteredReportData);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {' '}
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('Returned Sale Item Summary Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tCommon('Search by bill no, name')}
              />
              <Tooltip title="Search" placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.dateDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="RETURNED_BILL_ITEM_SUMMARY"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Terminal')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterTerminal}
                  onChange={(e) => setFilterTerminal(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterTerminal) => {
                    if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterTerminal.map((id) => terminals.find((tr) => tr.id === id).name);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {terminals.map((terminal) => (
                    <MenuItem key={terminal.id} value={terminal.id}>
                      <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                      <ListItemText primary={terminal.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Salesperson')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterSalesperson}
                  onChange={(e) => setFilterSalesperson(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterSalesperson) => {
                    if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterSalesperson.map(
                      (id) => salesPersons.find((person) => person.id === id).name
                    );
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {salesPersons.map((salesperson) => (
                    <MenuItem key={salesperson.id} value={salesperson.id}>
                      <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                      <ListItemText primary={salesperson?.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Tax')}</label>
            </div>
            <div>
              <TextField
                select
                name="category"
                size="small"
                variant="outlined"
                defaultValue={filterTax}
                color="primary"
                className={styles.textfeild}
                onChange={(event) => {
                  setFilterTax(event.target.value);
                }}
              >
                {taxes &&
                  taxes.map((vat) => (
                    <MenuItem value={vat} key={vat}>
                      {vat}
                    </MenuItem>
                  ))}
                <MenuItem value="all">{tCommon('All')}</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Category')}</label>
            </div>
            <div>
              <TextField
                select
                name="filterTax"
                size="small"
                variant="outlined"
                defaultValue={filterCategory}
                color="primary"
                className={styles.textfeild}
                onChange={(event) => {
                  setFilterCategory(event.target.value);
                }}
              >
                {categories &&
                  categories.map((category) => (
                    <MenuItem value={category.id} key={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                <MenuItem value="all">{tCommon('All')}</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Item')}</label>
            </div>
            <ItemSearchSelect filterItem={filterItem} setFilterItem={setFilterItem} />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Bill No Sort')}</label>
            </div>
            <div>
              <TextField
                select
                name="billNo"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 135 }}
                value={sorting}
                onChange={(event) => {
                  setSorting(event.target.value);
                }}
                displayEmpty
              >
                <MenuItem value={null}>
                  <em>None</em>
                </MenuItem>
                <MenuItem value={'DESC'}>{tCommon('Descending')}</MenuItem>
                <MenuItem value={'ASC'}>{tCommon('Ascending')}</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateItemisedBillReports}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {errorMsg && (
        <div className={styles.marginTop}>
          <Error title={errorMsg} />
        </div>
      )}

      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : filteredReportData && filteredReportData.length ? (
        <div className={styles.tableContainer}>
          <Grayout open={isShowGenerateButton} />
          <DataTable
            columns={headerData}
            rows={filteredReportData}
            rowKey="bill_no-plu"
            summary={{
              name: tCommon('Total'),
              total: `${shop?.currency || ''} ${summary?.totalAmount}`,
              tax_amount: `${shop?.currency || ''} ${summary.totalTax}`,
              cess_amount: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
            }}
          />
        </div>
      ) : (
        !loadingIndicator && (
          <Info
            severity="warning"
            title={tReport('Oh no, there are no sales matching to your filter')}
            content={tReport(
              'There are no sales matching to your filter. Please verify your inputs to generate report.'
            )}
          />
        )
      )}

      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(ReturnedBillItemSummaryReport);
